import { classnames } from 'helpers/utils';
import { CSSProperties, useState } from 'react';
import { MEDIA_COUNT } from './onboarding-media';
import { videoPlaceholderMarkup } from './scenes/scene-markup';
import type { SceneMedia } from './scenes/scene-markup';

const placeholderArt = videoPlaceholderMarkup();

const PlayIcon = () => (
  <svg
    width="21"
    height="21"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polygon points="6 3 20 12 6 21 6 3" />
  </svg>
);

const Still = ({ media }: { media?: SceneMedia }) => {
  const [failed, setFailed] = useState(false);
  const image = media?.image && !failed ? media.image : null;
  return (
    <span
      className={classnames('preamble-video-still', {
        'is-unavailable': !image,
      })}
    >
      {image ? (
        <img src={image} alt="" onError={() => setFailed(true)} />
      ) : (
        <span
          className="preamble-video-fallback"
          aria-hidden="true"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: placeholderArt }}
        />
      )}
      {media?.kind === 'video' && (
        <span className="preamble-video-play">
          <PlayIcon />
        </span>
      )}
      {media?.kind === 'video' && media.duration && (
        <span className="preamble-video-duration">{media.duration}</span>
      )}
    </span>
  );
};

const kindLabel = (item: SceneMedia) => {
  if (item.kind !== 'video') return 'Photo';
  return item.sample ? 'Video' : 'Video recording';
};

// The welcome's four cards: the user's own pictures first, placeholders for
// the rest (all four when the vault has no pictures).
export const WelcomeCards = ({ media }: { media: (SceneMedia | null)[] }) => (
  <div className="intro-recordings">
    <div
      className="preamble-recordings is-four"
      role="region"
      aria-label={
        media.some((item) => item && !item.sample)
          ? 'Pictures from your Vault'
          : 'Your Vault'
      }
    >
      {Array.from({ length: MEDIA_COUNT }, (_, index) => {
        const item = media[index];
        if (!item)
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`placeholder-${index}`}
              className="preamble-recording is-placeholder"
              style={{ '--record-order': index } as CSSProperties}
              aria-hidden="true"
            >
              <Still />
              <span className="preamble-video-caption">
                <strong>
                  <span className="preamble-skeleton long" />
                  <span className="preamble-skeleton short" />
                </strong>
                <span>
                  <span className="preamble-skeleton meta" />
                </span>
              </span>
            </div>
          );
        const title =
          item.title || (item.kind === 'video' ? 'Video recording' : 'Photo');
        return (
          <div
            key={item.id}
            className="preamble-recording"
            data-memory-id={item.sample ? undefined : item.id}
            style={{ '--record-order': index } as CSSProperties}
            role="img"
            aria-label={title}
          >
            <Still media={item} />
            <span className="preamble-video-caption">
              <strong>{title}</strong>
              <span>{kindLabel(item)}</span>
            </span>
          </div>
        );
      })}
    </div>
  </div>
);
